/**
 * Extracto del abstract (#21). Los abstracts tienen copyright de las editoriales: salvo que su
 * licencia sea abierta, no se guardan ni se muestran completos, solo la frase clave (las
 * conclusiones si están marcadas; si no, las últimas frases) y el enlace al original.
 */

import type { Paper } from "./models";

export const EXCERPT_MAX = 300;

/** Licencias que permiten mostrar el abstract completo sin modificarlo: Creative Commons y dominio público. */
export function isOpenLicense(license: string | null | undefined): boolean {
  return /\bcc[\s-]?(by|0|zero)\b|creative commons|public[\s-]?domain|\bpd\b/i.test(license ?? "");
}

const CONCLUSION = /\b(?:conclusions?|in conclusion|interpretation|implications|we conclude|these (?:results|findings) (?:suggest|indicate|show))\b\s*[:.-]?\s*/i;

/**
 * Frases del texto: corte en . ! ? seguidos de espacio y mayúscula, número o paréntesis, o fin.
 * Un punto que no cumple eso (decimales como «P < 0.05», «e.g.») sigue dentro de la frase (#76).
 */
function sentences(text: string): string[] {
  return (
    text
      .match(/(?:[^.!?]|[.!?]+(?!\s+[A-Z0-9(]|\s*$))+(?:[.!?]+(?=\s+[A-Z0-9(]|\s*$)|$)/g)
      ?.map((s) => s.trim())
      .filter(Boolean) ?? [text]
  );
}

/** Recorta a `max` caracteres por frase o, si una sola no cabe, por palabra, con «…». */
function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  let out = "";
  for (const s of sentences(text)) {
    if ((out ? `${out} ${s}` : s).length > max) break;
    out = out ? `${out} ${s}` : s;
  }
  if (out) return out;
  const cut = text.slice(0, max - 1);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), max / 2)).trimEnd()}…`;
}

/** Extracto de hasta `max` caracteres: las conclusiones si el abstract las marca; si no, el final. */
export function excerpt(abstract: string, max = EXCERPT_MAX): string {
  const text = abstract.replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  const m = CONCLUSION.exec(text);
  // Solo si la marca está en la segunda mitad (no es el objetivo del estudio)
  if (m && m.index > text.length * 0.4) return clip(text.slice(m.index + m[0].length).replace(/^[a-z]/, (c) => c.toUpperCase()), max);
  // Sin marca: las últimas frases, que suelen ser la conclusión
  const all = sentences(text);
  let out = "";
  for (let i = all.length - 1; i >= 0; i--) {
    const next = out ? `${all[i]} ${out}` : all[i]!;
    if (next.length > max) break;
    out = next;
  }
  return out || clip(all.at(-1) ?? text, max);
}

/** El paper tal como se puede guardar o mostrar: abstract completo solo con licencia abierta. */
export function withPublicAbstract(p: Paper): Paper {
  if (isOpenLicense(p.license) || !p.abstract || p.abstract.length <= EXCERPT_MAX) return p;
  return { ...p, abstract: excerpt(p.abstract), abstract_is_excerpt: true };
}
